/**
 * Platform-branding hook.
 *
 * Fetches the platform's logo URL + display name + support email from
 * the unauthenticated `/platform/branding` endpoint and exposes them
 * to React components. The endpoint reads from the same Super Admin
 * Settings page that controls the rest of the platform — so when an
 * admin uploads a new logo, every consumer of this hook re-renders
 * with the new URL after the cache invalidates.
 *
 * Why this is unauthenticated:
 *   - Pre-auth surfaces (SignIn, SignUp, ForgotPassword, Reset,
 *     MaintenanceScreen, NotFoundPage) need the logo BEFORE any
 *     cookie / session exists.
 *   - The data is non-sensitive (just brand assets visible to every
 *     visitor of the marketing site too).
 *
 * Caching:
 *   - `staleTime: 5 minutes` — the logo changes rarely; refetching
 *     more often is wasted bandwidth.
 *   - The Settings page's logo-upload mutation invalidates this
 *     query (`queryKey: ['platform-branding']`) on success so the
 *     post-upload re-render shows the new logo without a hard
 *     refresh.
 *   - The endpoint sends `Cache-Control: no-store` so the browser
 *     never holds onto a stale response. Without that, the prior
 *     `max-age=60` made the post-upload invalidation a no-op — the
 *     refetch hit the browser cache and returned the OLD logo URL
 *     for up to 60 seconds. TanStack Query's staleTime above is the
 *     only meaningful dedup layer now, which is fine because the
 *     endpoint is one indexed read.
 *
 * Fallback behaviour:
 *   - When the endpoint fails (network blip, API down), the consuming
 *     `<Logo>` component falls back to the bundled `/logo.png` from
 *     the app's `public/` folder. Worst case = the original
 *     pre-feature logo, never a broken image.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from './api'

export const PLATFORM_BRANDING_QUERY_KEY = ['platform-branding']

export function usePlatformBranding() {
  return useQuery({
    queryKey: PLATFORM_BRANDING_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get('/platform/branding')
      // Endpoint always returns 200 with shape
      // { logoUrl, name, supportEmail }. We accept an empty logoUrl
      // and the <Logo> component falls back to the bundled asset.
      return {
        logoUrl: typeof res.data?.logoUrl === 'string' ? res.data.logoUrl : '',
        name: typeof res.data?.name === 'string' ? res.data.name : 'TaskMagix',
        supportEmail:
          typeof res.data?.supportEmail === 'string'
            ? res.data.supportEmail
            : '',
      }
    },
    staleTime: 5 * 60_000,
    // Don't retry hard — the bundled `/logo.png` fallback is fine
    // until the next natural refetch.
    retry: 1,
    refetchOnWindowFocus: false,
  })
}
